/**
 * Layout jedné řady dnů kalendáře — buď **týdne** (7 sloupců přes celou plochu), nebo **souvislé osy**
 * (všechny dny rozsahu, pevná šířka dne v px, vodorovný posuv).
 *
 * Je to čistá funkce nad rozsahy termínů: dostane dny řady, změřenou šířku dne a termíny, vrátí
 * hotové souřadnice buněk, pruhů a boxů dopravy. Žádný DOM, žádný React — proto se dá testovat
 * i ladit bez prohlížeče.
 *
 * ## Jak se pruh skládá
 *
 * 1. **Segmenty.** Termín se ořízne na řadu; přesah na obou stranách se pozná podle `openStart`
 *    / `openEnd` a pruh na té hraně dostane roztrženou (cik-cak) hranu.
 * 2. **Kotvy.** Doprava tam vyrůstá z **poloviny prvního dne**, doprava zpět z **poloviny posledního
 *    dne**. U jednodenního termínu se pravidlo obrací — oba směry vyrůstají z poloviny téhož dne
 *    od sebe, ne k sobě.
 * 3. **Místo.** Box smí přerůst svůj segment jen přes dny, které nezabírá jiný termín (`freeStart` /
 *    `freeEnd`). Když se do daného směru nevejde, překlopí se na druhou hranu segmentu a roste ven.
 *    Když ani po překlopení není místo, box se **nemačká** — přeteče a dny, na které přetekl, si pruh
 *    připíše, takže je tetris uvolní ostatním termínům.
 * 4. **Svazky.** Boxy, které se skutečně vodorovně kryjí, se skládají pod sebe a středí se jako celek;
 *    uvnitř svazku drží střed hlavní doprava a navazující linky se rozdělí nad ni a pod ni.
 * 5. **Název.** Popisek termínu leží jako vrstva pod boxy — vezme si nejširší volnou mezeru; když
 *    žádná není, dostane vlastní řádku nad nimi.
 * 6. **Skládání pruhů.** V týdnu tetris (každý pruh si drží svou výšku a sedne na ty, které protíná),
 *    na ose dráhy (lanes), aby se pruhy neřetězily do nekonečna.
 */
import { dateOfDay } from './days';
import type { TransportLegView } from './transportPlan';
import type { CalendarTermView } from './terms';

/** Výška jednoho boxu dopravy. */
const CHIP_HEIGHT = 28;
/** Svislá mezera mezi boxy ve svazku. */
const CHIP_GAP = 2;
/** Výška řádky s názvem termínu, když pro něj není volná mezera mezi boxy. */
const TITLE_HEIGHT = 20;
/** Svislé odsazení obsahu pruhu. */
const PAD_TOP = 3;
/** Kolik px si rezervuje box hlavní dopravy / navazující linky. */
const NEED_PRIMARY = 132;
const NEED_FEEDER = 128;
/** Jednodenní termín — dva primární boxy v jednom dni, tedy užší rezervace. */
const NEED_PRIMARY_ONE_DAY = 112;
const NEED_FEEDER_ONE_DAY = 104;
/** Nejmenší mezera, do které se ještě vejde název termínu. */
const NAME_MIN = 54;
/** Svislá mezera mezi pruhy. */
const BAR_GAP = 4;
/** Minimální výška plochy s pruhy (prázdný týden nesmí zkolabovat). */
const MIN_BARS_HEIGHT = 20;
/** Šířka roztržené hrany pruhu. */
const TEAR_WIDTH = 7;

export type ChipAlign = 'flex-start' | 'flex-end' | 'center';

/** Jedna skupina boxů dopravy usazená v pruhu. */
export interface ChipZone {
  key: string;
  top: number;
  left: string;
  right: string;
  align: ChipAlign;
  items: TransportLegView[];
}

/** Jeden den v hlavičce řady. */
export interface RowDayCell {
  key: string;
  day: number;
  /** Číslo dne; první den měsíce se píše jako `1. 7.`, aby byl na ose poznat přelom. */
  label: string;
  /** Index dne v týdnu (0 = pondělí) — na ose se z něj bere zkratka. */
  weekday: number;
  isToday: boolean;
}

/** Blok měsíce v pruhu nad dny (jen souvislá osa). */
export interface MonthSpan {
  key: string;
  /** Šířka bloku v px. */
  width: number;
  label: string;
}

/** Pruh jednoho termínu v jedné řadě. */
export interface TermBarModel {
  key: string;
  termId: string;
  label: string;
  title: string;
  nightsLabel: string;
  /** CSS `left` / `width` pruhu v rámci řady. */
  left: string;
  width: string;
  top: number;
  height: number;
  /** Pruh pokračuje z předchozí řady / do další — roztržená hrana. */
  openStart: boolean;
  openEnd: boolean;
  radius: string;
  /** Umístění popisku uvnitř pruhu. */
  nameLeft: string;
  nameWidth: string;
  namePadding: string;
  titleTop: number;
  titleHeight: number;
  zones: ChipZone[];
  /** Body cik-cak hrany (`polyline points`) a její `viewBox`. */
  tearViewBox: string;
  tearPointsStart: string;
  tearPointsEnd: string;
}

/** Hotová řada dnů. */
export interface CalendarRowModel {
  key: string;
  /** Číslo prvního dne řady. */
  from: number;
  /** Počet dnů řady — 7 u týdne, jinak souvislá osa. */
  cols: number;
  /** Je to souvislá osa (ne týden)? */
  isAxis: boolean;
  cells: RowDayCell[];
  bars: TermBarModel[];
  barsHeight: number;
  height: number;
  gridTemplateColumns: string;
  /** Pruh měsíců nad dny — jen na ose. */
  monthSpans: MonthSpan[] | null;
  /** Šířka řady (`auto` v týdnu, px na ose). */
  width: string;
}

export interface RowLayoutInput {
  terms: readonly CalendarTermView[];
  /** Číslo prvního dne řady. */
  from: number;
  /** Počet dnů řady. */
  cols: number;
  /** Šířka jednoho dne v px; `0` = ještě nezměřeno (pruhy se pak skládají hrubě podle dnů). */
  dayWidth: number;
  /** Číslo dnešního dne — zvýrazněná buňka. */
  today: number;
  /**
   * Režim „Reálná doprava“: každý spoj sedí na dnech, kdy skutečně jede (spoj s −1d/+1d se vystředí
   * na hranici obou dnů). Vypnuto = „Logická doprava“, tedy doprava u termínu, ke kterému patří.
   */
  realTime: boolean;
  /** Popisky měsíců pro pruh nad dny (jen osa). */
  monthNames: readonly string[];
  /** Jak se píše počet nocí u pruhu. */
  formatNights: (nights: number) => string;
}

interface Segment {
  term: CalendarTermView;
  from: number;
  to: number;
}

interface Placement {
  anchor: number;
  dir: 'l' | 'r';
  limit: number;
  need: number;
}

interface Unit {
  key: string;
  items: TransportLegView[];
  /** Rezervovaný vodorovný rozsah v px. */
  x0: number;
  x1: number;
  align: ChipAlign;
  kind: 'P' | 'F';
  left: string;
  right: string;
  height: number;
  /** Odhad skutečné šířky boxu (rezervace je záměrně širší). */
  actX0: number;
  actX1: number;
  top: number;
}

interface Cluster {
  members: Unit[];
  x0: number;
  x1: number;
  height: number;
  dead?: boolean;
}

interface PackedBar extends TermBarModel {
  /** Sloupce, které si pruh připsal (včetně přetečení boxů) — hrubá záloha bez změřeného dne. */
  col: number;
  colEnd: number;
  /** Pixelový rozsah pruhu včetně boxů — podle něj se balí dráhy a tetris. */
  pxStart: number | null;
  pxEnd: number | null;
  y: number;
}

function pct(value: number, cols: number): string {
  return `${(value / cols) * 100}%`;
}

/** Popisky měsíců nad dny — jeden blok na měsíc. */
function buildMonthSpans(
  from: number,
  cols: number,
  dayWidth: number,
  names: readonly string[]
): MonthSpan[] {
  const spans: MonthSpan[] = [];
  let count = 0;
  for (let index = 0; index < cols; index += 1) {
    const date = dateOfDay(from + index);
    const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    const last = spans[spans.length - 1];
    if (last && last.key === key) {
      count += 1;
      last.width = count * dayWidth;
      continue;
    }
    count = 1;
    spans.push({
      key,
      width: dayWidth,
      label: `${names[date.getUTCMonth()] ?? ''} ${date.getUTCFullYear()}`,
    });
  }
  return spans;
}

function buildCells(from: number, cols: number, today: number): RowDayCell[] {
  const cells: RowDayCell[] = [];
  for (let index = 0; index < cols; index += 1) {
    const day = from + index;
    const date = dateOfDay(day);
    const isFirst = date.getUTCDate() === 1;
    cells.push({
      key: String(day),
      day,
      label: isFirst ? `1. ${date.getUTCMonth() + 1}.` : String(date.getUTCDate()),
      weekday: ((day % 7) + 7 + 3) % 7,
      isToday: day === today,
    });
  }
  return cells;
}

/**
 * Umístění boxů jednoho směru: kotva + směr růstu, s překlopením, když v daném směru není místo.
 * Ani po překlopení se box nemačká — přeteče a dny si připíše pruh (viz hlavička souboru).
 */
function plan(
  anchor: number,
  dir: 'l' | 'r',
  limit: number,
  flipAnchor: number,
  flipLimit: number,
  need: number,
  dayWidth: number,
  cols: number
): Placement {
  const room = (dir === 'r' ? limit - anchor : anchor - limit) * dayWidth;
  const placement: Placement =
    dayWidth && room < need
      ? { anchor: flipAnchor, dir: dir === 'r' ? 'l' : 'r', limit: flipLimit, need }
      : { anchor, dir, limit, need };

  const room2 =
    (placement.dir === 'r'
      ? placement.limit - placement.anchor
      : placement.anchor - placement.limit) * dayWidth;
  if (dayWidth && room2 < need) {
    placement.limit =
      placement.dir === 'r'
        ? Math.min(cols, placement.anchor + need / dayWidth)
        : Math.max(0, placement.anchor - need / dayWidth);
  }
  return placement;
}

/** Jednodenní termín: pevné umístění bez překlápění — box drží svou šířku u poloviny dne. */
function fixedAt(anchor: number, dir: 'l' | 'r', need: number, dayWidth: number): Placement {
  const days = need / (dayWidth || 1);
  return { anchor, dir, limit: dir === 'r' ? anchor + days : anchor - days, need };
}

function spanOf(placement: Placement, dayWidth: number): { x0: number; x1: number } {
  const anchor = placement.anchor * dayWidth;
  const limit = placement.limit * dayWidth;
  return placement.dir === 'r'
    ? { x0: anchor, x1: Math.min(limit, anchor + placement.need) }
    : { x0: Math.max(limit, anchor - placement.need), x1: anchor };
}

/**
 * Když navazující boxy po překlopení skončí PŘES box hlavní dopravy, posadí se vedle něj (těsně za
 * jeho vzdálenější hranu, stejným směrem) — jinak by se musely skládat pod sebe a pruh by kvůli
 * tomu vyrostl o řádku. Vedle jen tehdy, když se tam celý vejdou; jinak zůstane původní řešení.
 */
function seatBeside(
  primary: Placement,
  feeder: Placement,
  hasPrimary: boolean,
  hasFeeder: boolean,
  dayWidth: number,
  cols: number,
  oneDay: boolean
): Placement {
  if (!dayWidth || oneDay || !hasPrimary || !hasFeeder) return feeder;
  const edge = (p: Placement, width: number) => {
    const anchor = p.anchor * dayWidth;
    return p.dir === 'r' ? { x0: anchor, x1: anchor + width } : { x0: anchor - width, x1: anchor };
  };
  const a = edge(primary, 104);
  const b = edge(feeder, 112);
  if (!(b.x0 < a.x1 - 2 && a.x0 < b.x1 - 2)) return feeder;

  const room = primary.dir === 'r' ? cols * dayWidth - (a.x1 + 3) : a.x0 - 3;
  if (room < 116) return feeder;

  const anchor = primary.dir === 'r' ? (a.x1 + 3) / dayWidth : (a.x0 - 3) / dayWidth;
  return {
    anchor,
    dir: primary.dir,
    need: NEED_FEEDER,
    limit:
      primary.dir === 'r'
        ? Math.min(cols, anchor + NEED_FEEDER / dayWidth)
        : Math.max(0, anchor - NEED_FEEDER / dayWidth),
  };
}

/**
 * Odhad SKUTEČNÉ šířky boxu — rezervace `NEED_*` je záměrně širší, takže bez tohoto odhadu by se
 * svisle skládaly i bloky, které se ve skutečnosti vůbec nekryjí.
 */
function actualWidth(unit: Unit): number {
  const item = unit.items[0];
  const hasOffset = Boolean(item?.departure?.dayOffset || item?.arrival?.dayOffset);
  return 100 + (hasOffset ? 20 : 0) + (unit.kind === 'F' ? 8 : 0);
}

/** Boxy jednoho směru usazené na skutečné dny („Reálná doprava“). */
function seatRealChips(
  items: readonly TransportLegView[],
  base: number,
  need: number,
  growDir: 'l' | 'r',
  keyPrefix: string,
  kind: 'P' | 'F',
  dayWidth: number,
  cols: number,
  units: Unit[]
): void {
  const rowWidth = cols * dayWidth;
  items.forEach((chip, index) => {
    const depOffset = chip.departure?.dayOffset ?? 0;
    const arrOffset = chip.arrival?.dayOffset ?? 0;
    const low = base + Math.min(depOffset, arrOffset);
    const high = base + Math.max(depOffset, arrOffset);

    let x0: number;
    let x1: number;
    let align: ChipAlign;
    if (high > low) {
      // Spoj přes půlnoc — vystředí se na hranici obou dnů.
      const center = Math.min(Math.max(high * dayWidth, need / 2), rowWidth - need / 2);
      x0 = center - need / 2;
      x1 = center + need / 2;
      align = 'center';
    } else if (low > base) {
      const start = Math.min(low * dayWidth + 3, rowWidth - need);
      x0 = start;
      x1 = start + need;
      align = 'flex-start';
    } else if (low < base) {
      const end = Math.max((low + 1) * dayWidth - 3, need);
      x0 = end - need;
      x1 = end;
      align = 'flex-end';
    } else if (growDir === 'r') {
      const start = Math.min((low + 0.5) * dayWidth + 3, rowWidth - need);
      x0 = start;
      x1 = start + need;
      align = 'flex-start';
    } else {
      const end = Math.max((low + 0.5) * dayWidth - 3, need);
      x0 = end - need;
      x1 = end;
      align = 'flex-end';
    }

    units.push({
      key: `${keyPrefix}${index}`,
      items: [chip],
      x0,
      x1,
      align,
      kind,
      left: `${x0}px`,
      right: `${rowWidth - x1}px`,
      height: CHIP_HEIGHT,
      actX0: 0,
      actX1: 0,
      top: 0,
    });
  });
}

// funkce podle kroků by znamenal protahovat mezi nimi tucet mezivýsledků (kotvy, volný běh,
// rezervace, svazky) a čitelnost by klesla, ne stoupla. Kroky jsou popsané v hlavičce souboru.
function buildBar(
  segment: Segment,
  segments: readonly Segment[],
  input: RowLayoutInput
): PackedBar {
  const { from: rowFrom, cols, dayWidth, realTime } = input;
  const term = segment.term;

  const col = segment.from - rowFrom;
  const span = segment.to - segment.from + 1;
  const colEnd = col + span - 1;
  const openStart = segment.from > term.from;
  const openEnd = segment.to < term.to;
  const tiny = span === 1;
  const insetStart = tiny ? 3 : 6;
  // +1 kompenzuje vlastní vlasovou linku pravé hrany buňky dne
  const insetEnd = tiny ? 4 : 7;

  const anchorOut = col + 0.5;
  const anchorBack = colEnd + 0.5;

  const outPrimary = openStart ? [] : term.transport.out.primary;
  const outFeeders = openStart ? [] : term.transport.out.feeders;
  const backPrimary = openEnd ? [] : term.transport.back.primary;
  const backFeeders = openEnd ? [] : term.transport.back.feeders;

  const segStart = col;
  const segEnd = colEnd + 1;

  // Volný běh: box smí přerůst svůj segment jen přes dny, které nezabírá jiný termín.
  const busy: boolean[] = [];
  for (const other of segments) {
    if (other === segment) continue;
    for (let c = other.from - rowFrom; c <= other.to - rowFrom; c += 1) busy[c] = true;
  }
  let freeStart = segStart;
  let freeEnd = segEnd;
  while (freeStart > 0 && !busy[freeStart - 1]) freeStart -= 1;
  while (freeEnd < cols && !busy[freeEnd]) freeEnd += 1;

  const middle = (anchorOut + anchorBack) / 2;
  const stacked = span <= 4; // příliš úzké na to, aby oba směry seděly vedle sebe
  const outFar = !stacked && backPrimary.length ? middle : freeEnd;
  const backFar = !stacked && outPrimary.length ? middle : freeStart;

  // JEDNODENNÍ TERMÍN (0 nocí): pravidlo se obrací — doprava tam sedí v LEVÉ polovině dne odjezdu,
  // doprava zpět v PRAVÉ. Oba směry tak vyrůstají z poloviny téhož dne od sebe, ne k sobě.
  const oneDay = term.nights === 0;
  const needPrimary = oneDay ? NEED_PRIMARY_ONE_DAY : NEED_PRIMARY;
  const needFeeder = oneDay ? NEED_FEEDER_ONE_DAY : NEED_FEEDER;

  const placeOutPrimary = oneDay
    ? fixedAt(anchorOut, 'l', needPrimary, dayWidth)
    : plan(anchorOut, 'r', outFar, segEnd, freeStart, NEED_PRIMARY, dayWidth, cols);
  let placeOutFeeder = oneDay
    ? fixedAt(anchorOut, 'l', needFeeder, dayWidth)
    : plan(anchorOut, 'l', freeStart, segStart, outFar, NEED_FEEDER, dayWidth, cols);
  const placeBackPrimary = oneDay
    ? fixedAt(anchorBack, 'r', needPrimary, dayWidth)
    : plan(anchorBack, 'l', backFar, segStart, freeEnd, NEED_PRIMARY, dayWidth, cols);
  let placeBackFeeder = oneDay
    ? fixedAt(anchorBack, 'r', needFeeder, dayWidth)
    : plan(anchorBack, 'r', freeEnd, segEnd, backFar, NEED_FEEDER, dayWidth, cols);

  placeOutFeeder = seatBeside(
    placeOutPrimary,
    placeOutFeeder,
    outPrimary.length > 0,
    outFeeders.length > 0,
    dayWidth,
    cols,
    oneDay
  );
  placeBackFeeder = seatBeside(
    placeBackPrimary,
    placeBackFeeder,
    backPrimary.length > 0,
    backFeeders.length > 0,
    dayWidth,
    cols,
    oneDay
  );

  const bandX0 = segStart * dayWidth;
  const bandX1 = segEnd * dayWidth;

  let units: Unit[] = [];
  if (realTime && dayWidth) {
    seatRealChips(outPrimary, col, needPrimary, oneDay ? 'l' : 'r', `${term.termId}-rop`, 'P', dayWidth, cols, units); // prettier-ignore
    seatRealChips(
      outFeeders,
      col,
      needFeeder,
      'l',
      `${term.termId}-rof`,
      'F',
      dayWidth,
      cols,
      units
    );
    seatRealChips(backPrimary, colEnd, needPrimary, oneDay ? 'r' : 'l', `${term.termId}-rbp`, 'P', dayWidth, cols, units); // prettier-ignore
    seatRealChips(
      backFeeders,
      colEnd,
      needFeeder,
      'r',
      `${term.termId}-rbf`,
      'F',
      dayWidth,
      cols,
      units
    );
  } else {
    const groups: Array<[string, TransportLegView[], Placement, 'P' | 'F']> = [
      [`${term.termId}-op`, outPrimary, placeOutPrimary, 'P'],
      [`${term.termId}-of`, outFeeders, placeOutFeeder, 'F'],
      [`${term.termId}-bp`, backPrimary, placeBackPrimary, 'P'],
      [`${term.termId}-bf`, backFeeders, placeBackFeeder, 'F'],
    ];
    for (const [key, items, placement, kind] of groups) {
      if (items.length === 0) continue;
      const toRight = placement.dir === 'r';
      const box = spanOf(placement, dayWidth);
      units.push({
        key,
        items,
        x0: box.x0,
        x1: box.x1,
        align: toRight ? 'flex-start' : 'flex-end',
        kind,
        left: toRight
          ? `calc(${pct(placement.anchor, cols)} + 3px)`
          : `calc(${pct(placement.limit, cols)} + 2px)`,
        right: toRight
          ? `calc(${pct(cols - placement.limit, cols)} + 2px)`
          : `calc(${pct(cols - placement.anchor, cols)} + 3px)`,
        height: CHIP_HEIGHT,
        actX0: 0,
        actX1: 0,
        top: 0,
      });
    }
  }

  // Každý box = vlastní řádka (svazek se pak dá rozdělit nad a pod hlavní dopravu).
  units = units.flatMap((unit) =>
    unit.items.length < 2
      ? [unit]
      : unit.items.map((item, index) => ({ ...unit, key: `${unit.key}-${index}`, items: [item] }))
  );

  for (const unit of units) {
    const width = actualWidth(unit);
    const anchor =
      unit.align === 'flex-start'
        ? unit.x0
        : unit.align === 'flex-end'
          ? unit.x1
          : (unit.x0 + unit.x1) / 2;
    if (unit.align === 'flex-start') {
      unit.actX0 = anchor;
      unit.actX1 = Math.min(unit.x1, anchor + width);
    } else if (unit.align === 'flex-end') {
      unit.actX0 = Math.max(unit.x0, anchor - width);
      unit.actX1 = anchor;
    } else {
      unit.actX0 = Math.max(unit.x0, anchor - width / 2);
      unit.actX1 = Math.min(unit.x1, anchor + width / 2);
    }
  }

  // Svazky: jednotky, které se SKUTEČNĚ vodorovně kryjí, se skládají pod sebe.
  let clusters: Cluster[] = [];
  for (const unit of units) {
    let hit: Cluster | null = null;
    for (const cluster of clusters) {
      if (!(unit.actX0 < cluster.x1 - 2 && cluster.x0 < unit.actX1 - 2)) continue;
      if (hit) {
        hit.members = hit.members.concat(cluster.members);
        hit.x0 = Math.min(hit.x0, cluster.x0);
        hit.x1 = Math.max(hit.x1, cluster.x1);
        cluster.dead = true;
      } else {
        hit = cluster;
      }
    }
    clusters = clusters.filter((cluster) => !cluster.dead);
    if (hit) {
      hit.members.push(unit);
      hit.x0 = Math.min(hit.x0, unit.actX0);
      hit.x1 = Math.max(hit.x1, unit.actX1);
    } else {
      clusters.push({ members: [unit], x0: unit.actX0, x1: unit.actX1, height: 0 });
    }
  }

  let contentHeight = 0;
  for (const cluster of clusters) {
    cluster.height = cluster.members.reduce(
      (sum, member, index) => sum + member.height + (index ? CHIP_GAP : 0),
      0
    );
    contentHeight = Math.max(contentHeight, cluster.height);
  }

  // Název termínu si vezme nejširší volnou mezeru mezi boxy.
  let nameLeft: number | null = null;
  let nameWidth: number | null = null;
  if (dayWidth && units.length) {
    const boxes = units.map((unit) => ({ x0: unit.x0, x1: unit.x1 })).sort((a, b) => a.x0 - b.x0);
    let cursor = bandX0 + 2;
    let best: { x0: number; x1: number } | null = null;
    for (const box of [...boxes, { x0: bandX1 - 2, x1: bandX1 - 2 }]) {
      const gap = { x0: cursor, x1: Math.min(box.x0 - 2, bandX1 - 2) };
      if (gap.x1 - gap.x0 > (best ? best.x1 - best.x0 : 0)) best = gap;
      cursor = Math.max(cursor, box.x1 + 2);
    }
    if (best && best.x1 - best.x0 >= NAME_MIN) {
      nameLeft = best.x0 - bandX0;
      nameWidth = best.x1 - best.x0;
    }
  }

  const overlaid = nameWidth !== null;
  const titleBand = units.length === 0 ? TITLE_HEIGHT : overlaid ? 0 : TITLE_HEIGHT + 2;
  const bandTop = PAD_TOP + titleBand;
  const barHeight = bandTop + contentHeight + PAD_TOP;

  for (const cluster of clusters) {
    // Ve svazku drží střed HLAVNÍ doprava — navazující se rozdělí nad ni a pod ni, takže hlavní
    // box zůstane svisle vystředěný i když pruh naroste.
    const primary = cluster.members.filter((member) => member.kind === 'P');
    const connected = cluster.members.filter((member) => member.kind !== 'P');
    if (primary.length && connected.length) {
      const above = connected.slice(0, Math.floor(connected.length / 2));
      cluster.members = [...above, ...primary, ...connected.slice(above.length)];
    }
    let y = bandTop + Math.round((contentHeight - cluster.height) / 2);
    for (const member of cluster.members) {
      member.top = y;
      y += member.height + CHIP_GAP;
    }
  }

  // Pruh si připíše i dny, na kterých leží jen jeho doprava — tetris je pak ostatním uvolní.
  let claimedStart = col;
  let claimedEnd = colEnd;
  let pxStart = bandX0;
  let pxEnd = bandX1;
  if (dayWidth) {
    for (const unit of units) {
      claimedStart = Math.min(claimedStart, Math.max(0, Math.floor(unit.actX0 / dayWidth + 0.35)));
      claimedEnd = Math.max(
        claimedEnd,
        Math.min(cols - 1, Math.ceil(unit.actX1 / dayWidth - 0.35) - 1)
      );
      pxStart = Math.min(pxStart, unit.actX0);
      pxEnd = Math.max(pxEnd, unit.actX1);
    }
  }

  const tearStart: string[] = [];
  const tearEnd: string[] = [];
  for (let y = 0; y <= barHeight + BAR_GAP; y += 4) {
    const even = (y / 4) % 2 === 0;
    tearStart.push(`${even ? TEAR_WIDTH : 0},${y}`);
    tearEnd.push(`${even ? 0 : TEAR_WIDTH},${y}`);
  }

  return {
    key: `${term.termId}-${rowFrom}`,
    termId: term.termId,
    label: term.label,
    title: term.title,
    nightsLabel: input.formatNights(term.nights),
    left: `calc(${pct(col, cols)} + ${insetStart}px)`,
    width: `calc(${pct(span, cols)} - ${insetStart + insetEnd}px)`,
    top: 0,
    height: barHeight,
    openStart,
    openEnd,
    radius: [
      openStart ? '0' : '7px',
      openEnd ? '0' : '7px',
      openEnd ? '0' : '7px',
      openStart ? '0' : '7px',
    ].join(' '),
    nameLeft: overlaid ? `${nameLeft}px` : '0px',
    nameWidth: overlaid ? `${nameWidth}px` : '100%',
    namePadding: overlaid ? '0 2px' : '0 8px',
    titleTop: PAD_TOP,
    titleHeight: overlaid ? barHeight - 2 * PAD_TOP : TITLE_HEIGHT,
    zones: units.map((unit) => ({
      key: unit.key,
      items: unit.items,
      top: unit.top,
      left: unit.left,
      right: unit.right,
      align: unit.align,
    })),
    tearViewBox: `0 0 ${TEAR_WIDTH} ${barHeight}`,
    tearPointsStart: tearStart.join(' '),
    tearPointsEnd: tearEnd.join(' '),
    col: claimedStart,
    colEnd: claimedEnd,
    pxStart: dayWidth ? pxStart : null,
    pxEnd: dayWidth ? pxEnd : null,
    y: 0,
  };
}

/** Kryjí se dva pruhy? Bez změřené šířky dne padáme zpět na dny. */
function overlaps(a: PackedBar, b: PackedBar): boolean {
  if (a.pxStart !== null && b.pxStart !== null && a.pxEnd !== null && b.pxEnd !== null) {
    return a.pxStart < b.pxEnd - 2 && b.pxStart < a.pxEnd - 2;
  }
  return a.col <= b.colEnd && b.col <= a.colEnd;
}

/** Layout jedné řady dnů — viz hlavička souboru. */
export function layoutRow(input: RowLayoutInput): CalendarRowModel {
  const { from, cols, dayWidth, today, monthNames: names } = input;
  const isAxis = cols !== 7;
  const to = from + cols - 1;

  const segments: Segment[] = [];
  for (const term of input.terms) {
    const start = Math.max(term.from, from);
    const end = Math.min(term.to, to);
    if (end < start) continue;
    segments.push({ term, from: start, to: end });
  }
  segments.sort(
    (a, b) =>
      a.from - b.from || b.to - b.from - (a.to - a.from) || (a.term.termId < b.term.termId ? -1 : 1)
  );

  const bars = segments.map((segment) => buildBar(segment, segments, input));

  if (isAxis) {
    // Souvislá osa: pruhy sdílejí dráhy (lanes), jinak by se řetězily do nekonečna.
    const lanes: Array<{ items: PackedBar[]; height: number }> = [];
    for (const bar of bars) {
      let index = 0;
      for (;;) {
        const lane = (lanes[index] ??= { items: [], height: 0 });
        if (!lane.items.some((other) => overlaps(bar, other))) break;
        index += 1;
      }
      const lane = lanes[index]!;
      lane.items.push(bar);
      lane.height = Math.max(lane.height, bar.height);
    }
    let y = 0;
    for (const lane of lanes) {
      for (const bar of lane.items) {
        bar.y = y;
        bar.top = y;
      }
      y += lane.height + BAR_GAP;
    }
  } else {
    // Tetris: každý pruh si drží svou výšku a sedne na ty, které protíná.
    const placed: PackedBar[] = [];
    for (const bar of bars) {
      let y = 0;
      for (const other of placed) {
        if (overlaps(bar, other)) y = Math.max(y, other.y + other.height + BAR_GAP);
      }
      bar.y = y;
      bar.top = y;
      placed.push(bar);
    }
  }

  const barsHeight = Math.max(
    MIN_BARS_HEIGHT,
    bars.reduce((max, bar) => Math.max(max, bar.y + bar.height), 0)
  );

  return {
    key: String(from),
    from,
    cols,
    isAxis,
    cells: buildCells(from, cols, today),
    bars,
    barsHeight,
    height: (isAxis ? 43 : 22) + 6 + barsHeight + 4,
    gridTemplateColumns: isAxis ? `repeat(${cols}, ${dayWidth}px)` : 'repeat(7, 1fr)',
    monthSpans: isAxis ? buildMonthSpans(from, cols, dayWidth, names) : null,
    width: isAxis ? `${cols * dayWidth}px` : 'auto',
  };
}
